const transformCourses = response =>
  (response.data?.courses || []).map(({ id, name }) => ({ id, label: name }));

const normalizeStatus = status => {
  if (!status) return '—';

  const normalizedStatus = String(status).toLowerCase().replace(/\s+/g, '_');
  if (normalizedStatus === 'completed') return 'Completed';
  if (['not_completed', 'in_progress', 'incomplete'].includes(normalizedStatus)) {
    return 'In Progress';
  }

  return status;
};

const formatDate = value => {
  if (!value) return '—';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric'
  });
};

const formatDuration = value => {
  const totalSeconds = Number(value);
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return '—';

  const roundedSeconds = Math.floor(totalSeconds);
  const hours = Math.floor(roundedSeconds / 3600);
  const minutes = Math.floor((roundedSeconds % 3600) / 60);
  const seconds = roundedSeconds % 60;

  return `${hours} hr ${minutes} min ${seconds} sec`;
};

const formatPercentage = value => {
  if (value === undefined || value === null || value === '—') return '—';

  const normalizedValue = typeof value === 'string' ? value.replace(/%$/, '').trim() : value;
  const numericValue = Number(normalizedValue);

  if (!Number.isFinite(numericValue)) return String(value);
  return numericValue === 0 ? '0' : `${normalizedValue}%`;
};

const getCourseNames = row => {
  const courseNames = row.courseNames ?? row.course_names;
  const values = Array.isArray(courseNames) ? courseNames : [courseNames];

  return values
    .reduce((names, item) => {
      const value = typeof item === 'string' ? item : item?.name ?? item?.courseName;
      return typeof value === 'string' ? names.concat(value.split(',')) : names;
    }, [])
    .map(name => name.trim())
    .filter(Boolean);
};

const transformStudentCourseRow = (row, index) => {
  const student = row.student || {};
  const course = row.course || {};
  const branch = row.branch || {};
  const studentId = row.studentId ?? row.student_id ?? student.id ?? row.id;
  const courseId = row.courseId ?? row.course_id ?? row.learnwordsCourseId ?? course.id;
  const courseNames = getCourseNames(row);
  const totalCourses = Number(row.totalCourses ?? row.total_courses ?? 0);
  const fallbackCourseName =
    row.courseName ??
    row.course_name ??
    course.name ??
    (totalCourses ? `${totalCourses} course${totalCourses === 1 ? '' : 's'}` : '—');

  return {
    ...row,
    id: row.id ?? `${studentId || index}-${courseId || index}`,
    studentId,
    courseId,
    first: row.firstName ?? row.first_name ?? student.firstName ?? '',
    last: row.lastName ?? row.last_name ?? student.lastName ?? '',
    course: courseNames.join(', ') || fallbackCourseName,
    courseNames,
    programType: row.programType ?? row.program_type ?? '—',
    branch: row.branchName ?? row.branch_name ?? branch.name ?? ''
  };
};

const transformStudentCourses = response => {
  const container = response.data?.students || {};
  const rows = Array.isArray(container) ? container : container.students || [];
  const skip = Number(container.skip || 0);
  const limit = Number(container.limit || rows.length || 0);
  const reportedTotal = container.total ?? container.totalCount ?? container.total_count;
  const total = reportedTotal === undefined ? rows.length : Number(reportedTotal);

  return {
    data: rows.map(transformStudentCourseRow),
    total,
    hasMore:
      reportedTotal === undefined ? limit > 0 && rows.length === limit : skip + rows.length < total
  };
};

const transformStudentCourseDetails = response =>
  (response.data?.courses || []).map((course, index) => {
    const timeOnCourse = course.timeOnCourse ?? course.time_on_course;
    const numericTimeOnCourse = Number(timeOnCourse);

    return {
      ...course,
      id: course.courseId ?? course.course_id ?? index,
      courseId: course.courseId ?? course.course_id,
      name: course.courseName ?? course.course_name ?? '—',
      programType: course.programType ?? course.program_type ?? '—',
      studentName: course.studentName ?? course.student_name ?? '—',
      status: normalizeStatus(course.status),
      grade: course.grade ?? course.averageScoreRate ?? course.average_score_rate ?? '—',
      timeOnCourseSeconds:
        Number.isFinite(numericTimeOnCourse) && numericTimeOnCourse >= 0
          ? numericTimeOnCourse
          : 0,
      hours: formatDuration(timeOnCourse),
      progressRate: course.progressRate ?? course.progress_rate ?? 0,
      date: formatDate(course.completedAt ?? course.completed_at)
    };
  });

const transformStudentCourseSections = response =>
  [...(response.data?.sections || [])]
    .sort(
      (first, second) =>
        Number(first.sortOrder ?? first.sort_order ?? 0) -
        Number(second.sortOrder ?? second.sort_order ?? 0)
    )
    .map((section, index) => ({
      ...section,
      id: section.sectionId ?? section.section_id ?? index,
      name: section.sectionName ?? section.section_name ?? '—',
      progressRate: Number(
        section.sectionProgressRate ?? section.section_progress_rate ?? 0
      ),
      completedUnits: section.completedUnitsCount ?? section.completed_units_count ?? 0,
      timeOnSection: formatDuration(section.timeOnSection ?? section.time_on_section ?? 0),
      grade: section.grade ?? '—'
    }));

export {
  formatDuration,
  formatPercentage,
  transformCourses,
  transformStudentCourseDetails,
  transformStudentCourseSections,
  transformStudentCourses
};
